import React, { useEffect, useMemo, useState } from "react";
import config from "@/config";
import { useDispatch, useSelector } from "react-redux";
import { formatPhoneNumber } from "../../utils/Utils.js";
import {
  addToast,
  getDashboardActions,
  setSelectedMerchant,
} from "../../store/app/action.js";
import Badge from "../../components/Badges.jsx";
import Tooltip from "../../components/Tooltip.jsx";
import { useHotkeys } from "react-hotkeys-hook";
import { onboardChecklistItems } from "@/utils/Utils.js";
import { setProspectReadStatus } from "../../api/admin.js";
function StoreTableItem({ store, handleClick, setShowArchive, setShowManualPayout, setShowRequestLicenses, storeType , showProspectProgress, setShowProspectProgress, refreshStores }) {
  const dispatch = useDispatch();
  const { selectedLocation, merchants } = useSelector((state) => state.app);
  const contextRef = React.useRef();
  const [showContext, setShowContext] = useState(false);
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (contextRef.current && !contextRef.current.contains(event.target)) {
        setShowContext(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);
  const [isShiftDown, setIsShiftDown] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Shift") setIsShiftDown(true);
    };
    const handleKeyUp = (e) => {
      if (e.key === "Shift") setIsShiftDown(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  useHotkeys(
    "i",
    () => {
      if (!showContext) return;
      impersonate();
      setShowContext(false);
    },
    { preventDefault: true }
  );

  useHotkeys(
    "e",
    () => {
      if (!showContext) return;
      edit();
      setShowContext(false);
    },
    { preventDefault: true }
  );

  useHotkeys(
    "c",
    () => {
      if (!showContext) return;
      copyEasyID();
      setShowContext(false);
    },
    { preventDefault: true }
  );

  useHotkeys(
    "shift+c",
    () => {
      if (!showContext) return;
      copyID();
      setShowContext(false);
    },
    { preventDefault: true }
  );

  useHotkeys(
    "g",
    () => {
      if (!showContext) return;
      goToStore();
      setShowContext(false);
    },
    { preventDefault: true }
  );

  useHotkeys(
    "a",
    () => {
      if (!showContext) return;
      archive();
      setShowContext(false);
    },
    { preventDefault: true }
  );

  const viewProgress = () => {
    setShowProspectProgress(store);
    if (!store.prospect_unread) {
      return;
    }
    (async () => {
      try {
        await setProspectReadStatus(store.id, false);
        await dispatch(getDashboardActions());
        if (refreshStores) {
          refreshStores();
        }
      } catch (err) {
        dispatch(
          addToast({
            type: "error",
            message: `Could not mark ${store.name} as read`,
            timer: 5,
          })
        );
      }
    })();
  };
  const toggleProspectRead = async () => {
    const nextUnreadStatus = !Boolean(store.prospect_unread);
    try {
      await setProspectReadStatus(store.id, nextUnreadStatus);
      dispatch(
        addToast({
          type: "success",
          message: nextUnreadStatus
            ? `Marked ${store.name} as unread`
            : `Marked ${store.name} as read`,
          timer: 5,
        })
      );
      await dispatch(getDashboardActions());
      if (refreshStores) {
        refreshStores();
      }
    } catch (err) {
      dispatch(
        addToast({
          type: "error",
          message: `Failed to update read status for ${store.name}`,
          timer: 5,
        })
      );
    }
  };
  const impersonate = () => {
    
    dispatch(setSelectedMerchant(store.direct ? store.org : "000000000000000000000000"));
    dispatch({
      type: "SET_SELECTED_LOCATION",
      payload: store.id,
    });
    dispatch(
      addToast({
        type: "success",
        message: `Session set to ${store.name}`,
        timer: 5,
      })
    );
  };
  const edit = () => {
    handleClick(store.store_id);
  };
  const copyEasyID = () => {
    navigator.clipboard
      .writeText(`${store.name} (${store.store_id})`)
      .then(() => {
        dispatch(
          addToast({
            type: "success",
            message: "Copied StoreID + Name to clipboard",
            timer: 5,
          })
        );
      })
      .catch((err) => {
        dispatch(
          addToast({
            type: "error",
            message: "Failed to copy EasyID + Name to clipboard",
            timer: 5,
          })
        );
      });
  };
  const archive = () => {
    setShowArchive(store);
  };
  const goToStore = () => {
    window.open(
      store.custom_direct_web_urls?.[0]
        ? `https://${store.custom_direct_web_urls?.[0]}`
        : `${config.direct_web_prefix}${
            store.direct_web_url ?? store.store_id
          }`,
      "_blank"
    );
  };
  const copyID = () => {
    navigator.clipboard
      .writeText(`ObjectId('${store.id}')`)
      .then(() => {
        dispatch(
          addToast({
            type: "success",
            message: "Copied ID to clipboard",
            timer: 5,
          })
        );
      })
      .catch((err) => {
        dispatch(
          addToast({
            type: "error",
            message: "Failed to copy ID to clipboard",
            timer: 5,
          })
        );
      });
  };
  const statusBadge = useMemo(() => {
    if (storeType === "direct" && store.kill_switch) {
      return <Badge color="dark">Off Boarded</Badge>;
    }
    if (!store.enabled) {
      return <Badge color="red">Disabled</Badge>;
    }
    return <Badge color="green">Enabled</Badge>;
  }, [store, storeType]);
  const stripeBadge = useMemo(() => {
    if (!store) {
      return null;
    }

    switch (store.payment_gateway) {
      case "frame":
        const frameRequiredFields = ["frame_public_key", "frame_private_key"];
        if (frameRequiredFields.some((field) => !store[field])) {
          return (
            <div className="flex items-center gap-2">
              <Badge color="red">Incomplete Frame Config</Badge>
              <Tooltip>
                <>
                  The following fields must be present:
                  <ul>
                    {frameRequiredFields.map((field) => (
                      <li
                        key={field}
                        className={store[field] ? "" : "text-red-500"}
                      >
                        {field}
                      </li>
                    ))}
                  </ul>
                </>
              </Tooltip>
            </div>
          );
        }
        if (store.aggregated_transaction_fee_billing && merchants) {
          const org = merchants.find((m) => m.id === store.org);
          if (!org) {
            return <Badge color="red">Frame: No Org</Badge>;
          }
          if (!org.stripe_cust_id) {
            return <Badge color="red">Frame: No Stripe account</Badge>;
          } else if (!org.stripe_card_id) {
            return <Badge color="red">Frame: No card on file</Badge>;
          }
        }
        if (!store.aggregated_transaction_fee_billing) {
          if (!store.stripe_cust_id) {
            return <Badge color="red">Frame: No Stripe account</Badge>;
          }
          if (!store.stripe_card_id) {
            return <Badge color="red">Frame: No card on file</Badge>;
          }
        }
        return <Badge color="green">Frame: Ready</Badge>;
      case "nmi":
        const nmiRequiredFields = [
          "nmi_gateway_id",
          "nmi_public_key",
          "nmi_private_key",
        ];
        if (nmiRequiredFields.some((field) => !store[field])) {
          return (
            <div className="flex items-center gap-2">
              <Badge color="red">Incomplete NMI Config</Badge>
              <Tooltip>
                <>
                  The following fields must be present:
                  <ul>
                    {nmiRequiredFields.map((field) => (
                      <li
                        key={field}
                        className={store[field] ? "" : "text-red-500"}
                      >
                        {field}
                      </li>
                    ))}
                  </ul>
                </>
              </Tooltip>
            </div>
          );
        }
        if (store.aggregated_transaction_fee_billing && merchants) {
          const org = merchants.find((m) => m.id === store.org);
          if (!org) {
            return <Badge color="red">NMI: No Org</Badge>;
          }
          if (!org.stripe_cust_id) {
            return <Badge color="red">NMI: No Stripe account</Badge>;
          } else if (!org.stripe_card_id) {
            return <Badge color="red">NMI: No card on file</Badge>;
          }
        }
        if (!store.aggregated_transaction_fee_billing) {
          if (!store.stripe_cust_id) {
            return <Badge color="red">NMI: No Stripe account</Badge>;
          }
          if (!store.stripe_card_id) {
            return <Badge color="red">NMI: No card on file</Badge>;
          }
        }
        return <Badge color="green">NMI: Ready</Badge>;
      default:
        if (!store.stripe_account_id) {
          return <Badge color="red">Stripe: No Connect Account</Badge>;
        } else if (!store.stripe_verified) {
          return <Badge color="red">Stripe: Not Verified</Badge>;
        }
        return <Badge color="green">Stripe: Ready</Badge>;
    }
  }, [store, merchants]);
  const prospectStatus = useMemo(() => {
    if(storeType !== "direct prospects") return null;
    var badge = null;
    const totalSteps = onboardChecklistItems.length;
    const stepsTaken = (Object.keys(store.onboard_steps?.steps || {}).length) || 0;
    let stepsProgressBar = null;
    if(!store.onboard_steps){
      badge = <Badge color="blue">Not Started</Badge>;
    }else{
      
      if(store.onboard_steps.status === "submitted"){
        badge = <Badge color="yellow">Submitted</Badge>;
      }else if(store.onboard_steps.status === "rejected"){
        badge = <Badge color="red">Rejected</Badge>;
      }else if(store.onboard_steps.status === "scheduled_publish" ){
        badge = <Badge color="green">Scheduled Publish</Badge>;
      }else if(store.onboard_steps.status === "awaiting_manual_publish"){
        badge = <Badge color="green">Awaiting Manual Publish</Badge>;
      }else{
        badge = <Badge color="yellow">Working</Badge>;
        stepsProgressBar = <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-sm">
          <div className="h-full bg-indigo-500 dark:bg-indigo-400 rounded-sm" style={{ width: `${(stepsTaken / totalSteps) * 100}%` }}></div>
        </div>;
      }
    }
    return { badge, stepsProgressBar, stepsTaken, totalSteps };
  }, [store]);
  return (
    <tr>
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="flex flex-col items-start">
          <div className="font-medium text-slate-800 dark:text-slate-100 flex items-center gap-2">
            {storeType === "direct prospects" && store.prospect_unread && (
              <span
                className="h-2.5 w-2.5 rounded-full bg-red-500 inline-block"
                aria-label="Unread prospect"
                title="Unread prospect"
              />
            )}
            {store.store_id}{" "}
          </div>
          {store.store_id === selectedLocation?.store_id && (
            <Badge color="green">Current</Badge>
          )}
        </div>
      </td>
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-left">{store.name}</div>
      </td>
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-left">{store.email}</div>
      </td>
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-left">{formatPhoneNumber(store.phone)}</div>
      </td>
      {storeType === "direct prospects" && (
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          {prospectStatus?.badge}
          {prospectStatus?.stepsProgressBar && <div className="mt-2 text-xs w-36">{prospectStatus?.stepsProgressBar} {prospectStatus?.stepsTaken} / {prospectStatus?.totalSteps} steps taken</div>}
        </td>
      )}
      {storeType === "direct prospects" && (
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          {store.created
            ? new Date(store.created).toLocaleDateString()
            : "Not created"}
        {/* Add relative date under the created date */}
        {store.created && (
          <div className="text-xs text-slate-500">
            {(() => {
              const createdDate = new Date(store.created);
              const now = new Date();
              const diff = Math.floor((now - createdDate) / 1000);

              if (diff < 60) {
                return `${diff} seconds ago`;
              } else if (diff < 3600) {
                const mins = Math.floor(diff / 60);
                return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
              } else if (diff < 86400) {
                const hours = Math.floor(diff / 3600);
                return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
              } else {
                const days = Math.floor(diff / 86400);
                return `${days} day${days !== 1 ? "s" : ""} ago`;
              }
            })()}
          </div>
        )}
        </td>
      )}
      {storeType !== "direct prospects" && (
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          {stripeBadge}
        </td>
      )}
      {storeType !== "direct prospects" && (
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          {statusBadge}
        </td>
      )}
      {storeType === "marketplace-onboarding" && (
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          {(() => {
            if (store.onboarding_step === "under_review") {
              return <Badge color="blue">Under Review</Badge>;
            }
            const stepIndex = { legal: 1, stripe: 2, licenses: 3, menu: 4 };
            const stepLabel = { legal: "Legal", stripe: "Stripe", licenses: "Licenses", menu: "Menu" };
            const idx = stepIndex[store.onboarding_step] || 0;
            if (!idx) return <span className="text-slate-400 text-xs">Not started</span>;
            return (
              <div className="flex items-center gap-2">
                <Badge color="yellow">
                  {idx}/4 {stepLabel[store.onboarding_step]}
                </Badge>
              </div>
            );
          })()}
        </td>
      )}
      {storeType === "marketplace-onboarding" && (
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          {store.last_step_completed
            ? new Date(store.last_step_completed).toLocaleDateString()
            : <span className="text-slate-400 text-xs">—</span>}
          {store.last_step_completed && (
            <div className="text-xs text-slate-500">
              {(() => {
                const completed = new Date(store.last_step_completed);
                const diff = Math.floor((Date.now() - completed) / 1000);
                if (diff < 60) return `${diff}s ago`;
                if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                return `${Math.floor(diff / 86400)}d ago`;
              })()}
            </div>
          )}
        </td>
      )}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
        {/* Menu button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setShowContext(!showContext);
          }}
          className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400 rounded-full"
        >
          <span className="sr-only">Menu</span>
          <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="2" />
            <circle cx="10" cy="16" r="2" />
            <circle cx="22" cy="16" r="2" />
          </svg>
        </button>
        <div
          ref={contextRef}
          className={`absolute right-0 mt-2 w-96 py-2 z-10 bg-white dark:bg-theme-surface-accent rounded-sm shadow-lg border border-slate-200 dark:border-slate-700 ${
            showContext ? "block" : "hidden"
          }`}
        >
          {[
            { 
              name: "View Progress",
              onClick: viewProgress,
              shortcut: ["p"],
              hide: storeType !== "direct prospects",
            },
            {
              name: store.prospect_unread ? "Mark as read" : "Mark as unread",
              onClick: toggleProspectRead,
              hide: storeType !== "direct prospects",
            },
            {
              name: "Impersonate",
              onClick: impersonate,
              shortcut: ["i"],
            },
            {
              name: "Manual payout",
              onClick: () => setShowManualPayout(store),
              hide:
                storeType === "direct prospects" || !store.stripe_account_id,
            },
            {
              name: "Request license documents",
              onClick: () => setShowRequestLicenses(store),
              hide: storeType !== "marketplace",
            },
            {
              name: "Edit",
              onClick: edit,
              shortcut: ["e"],
              hide: storeType === "direct prospects",
            },
            {
              name: "Copy EasyID & Name to clipboard",
              onClick: copyEasyID,
              shortcut: ["c"],
            },
            isShiftDown && {
              name: "Copy ID to clipboard",
              onClick: copyID,
              shortcut: ["shift", "c"],
            },
            {
              name: "Go to store",
              onClick: goToStore,
              shortcut: ["g"],
            },
            {
              name: "Archive",
              onClick: archive,
              danger: true,
              shortcut: ["a"],
            },
          ]
            .filter((a) => (a && !a.hide))
            .map((a) => (
              <span
                key={a.name}
                onClick={(e) => {
                  if (a.disabled) return;
                  e.stopPropagation();
                  e.preventDefault();
                  setShowContext(false);
                  a.onClick();
                }}
                className={`flex items-center justify-between px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-900 ${
                  a.disabled
                    ? "text-slate-400 dark:text-slate-600 cursor-not-allowed"
                    : a.danger
                    ? "text-red-500 dark:text-red-500"
                    : "text-slate-800 dark:text-slate-100"
                }`}
              >
                {a.name}
                {a.shortcut && (
                  <span className="flex gap-1">
                    {a.shortcut.map((s, idx) => (
                      <kbd
                        key={idx}
                        className="inline-block px-1.5 py-0.5 rounded border text-xs font-mono text-slate-500 border-slate-300 dark:text-slate-400 dark:border-slate-600"
                      >
                        {s[0].toUpperCase() + s.slice(1)}
                      </kbd>
                    ))}
                  </span>
                )}
              </span>
            ))}
        </div>
      </td>
    </tr>
  );
}

export default StoreTableItem;
